import { useState, useEffect } from "react";
import { getClienti, createCliente, deleteCliente, updateCliente, bulkDeleteClienti } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Users, Search, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function ArchivioClienti() {
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    contatto: "",
    indirizzo: "",
  });

  useEffect(() => {
    loadClienti();
  }, []);

  const loadClienti = async () => {
    try {
      const response = await getClienti();
      setClienti(response.data);
      setSelectedIds([]);
    } catch (error) {
      console.error("Error loading clienti:", error);
      toast.error("Errore nel caricamento dei clienti");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }

    try {
      if (editingCliente) {
        await updateCliente(editingCliente.id, formData);
        toast.success("Cliente aggiornato con successo");
      } else {
        await createCliente(formData);
        toast.success("Cliente creato con successo");
      }
      
      setDialogOpen(false);
      setEditingCliente(null);
      setFormData({ nome: "", contatto: "", indirizzo: "" });
      loadClienti();
    } catch (error) {
      console.error("Error saving cliente:", error);
      toast.error("Errore nel salvataggio del cliente");
    }
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    setFormData({
      nome: cliente.nome,
      contatto: cliente.contatto || "",
      indirizzo: cliente.indirizzo || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteCliente(id);
      toast.success("Cliente eliminato con successo");
      setDeleteConfirm(null);
      loadClienti();
    } catch (error) {
      console.error("Error deleting cliente:", error);
      toast.error("Errore nell'eliminazione del cliente");
    }
  };

  const handleOpenDialog = () => {
    setEditingCliente(null);
    setFormData({ nome: "", contatto: "", indirizzo: "" });
    setDialogOpen(true);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredClienti.map((c) => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((i) => i !== id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteClienti(selectedIds);
      toast.success(`${selectedIds.length} clienti eliminati con successo`);
      setBulkDeleteConfirm(false);
      setSelectedIds([]);
      loadClienti();
    } catch (error) {
      console.error("Error bulk deleting clienti:", error);
      toast.error("Errore nell'eliminazione multipla");
    }
  };

  const filteredClienti = clienti.filter((c) =>
    c.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.contatto && c.contatto.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div data-testid="archivio-clienti-page" className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
            Archivio Clienti
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Gestisci l'anagrafica dei clienti
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              data-testid="nuovo-cliente-btn"
              className="btn-accent rounded-lg"
              onClick={handleOpenDialog}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-500" />
                {editingCliente ? "Modifica Cliente" : "Nuovo Cliente"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="nome" className="font-heading font-bold text-slate-700">
                  Nome *
                </Label>
                <Input
                  id="nome"
                  data-testid="cliente-nome-input"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Es: Azienda Verdi S.r.l."
                  className="rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contatto" className="font-heading font-bold text-slate-700">
                  Contatto
                </Label>
                <Input
                  id="contatto"
                  data-testid="cliente-contatto-input"
                  value={formData.contatto}
                  onChange={(e) => setFormData({ ...formData, contatto: e.target.value })}
                  placeholder="Es: 02 1234567 / info@azienda.it"
                  className="rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="indirizzo" className="font-heading font-bold text-slate-700">
                  Indirizzo
                </Label>
                <Input
                  id="indirizzo"
                  data-testid="cliente-indirizzo-input"
                  value={formData.indirizzo}
                  onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                  placeholder="Es: Via Milano 10, Roma"
                  className="rounded-sm"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="rounded-sm"
                >
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  data-testid="submit-cliente-btn"
                  className="btn-primary rounded-sm"
                >
                  {editingCliente ? "Salva Modifiche" : "Crea Cliente"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="card-industrial">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="search-clienti-input"
              placeholder="Cerca per nome o contatto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="card-industrial">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" />
              Elenco Clienti ({filteredClienti.length})
            </CardTitle>
            {selectedIds.length > 0 && (
              <Button
                data-testid="bulk-delete-clienti-btn"
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteConfirm(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina Selezionati ({selectedIds.length})
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {filteredClienti.length > 0 ? (
              <div className="table-scroll-wrapper">
                <div className="rounded-sm border border-slate-200 overflow-hidden">
                  <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-12">
                        <Checkbox
                          data-testid="select-all-clienti"
                          checked={filteredClienti.length > 0 && selectedIds.length === filteredClienti.length}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Nome
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Contatto
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Indirizzo
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Data Creazione
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-center">
                        Azioni
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClienti.map((cliente, index) => (
                      <TableRow 
                        key={cliente.id}
                        data-testid={`cliente-row-${index}`}
                        className={`hover:bg-slate-50/50 ${selectedIds.includes(cliente.id) ? "bg-orange-50" : ""}`}
                      >
                        <TableCell>
                          <Checkbox
                            data-testid={`select-cliente-${index}`}
                            checked={selectedIds.includes(cliente.id)}
                            onCheckedChange={(checked) => handleSelectItem(cliente.id, checked)}
                          />
                        </TableCell>
                        <TableCell className="font-medium text-slate-900">
                          {cliente.nome}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {cliente.contatto || "-"}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {cliente.indirizzo || "-"}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-500">
                          {formatDate(cliente.created_at)}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`edit-cliente-${index}`}
                              onClick={() => handleEdit(cliente)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`delete-cliente-${index}`}
                              onClick={() => setDeleteConfirm(cliente)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">
                  {searchTerm ? "Nessun cliente trovato" : "Nessun cliente registrato"}
                </p>
                <p className="text-sm mt-1">
                  {searchTerm ? "Prova con una ricerca diversa" : "Clicca su 'Nuovo Cliente' per iniziare"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare il cliente <span className="font-bold">{deleteConfirm?.nome}</span>?
              <br />
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-cliente-btn"
              onClick={() => handleDelete(deleteConfirm?.id)}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-red-600">Eliminazione Multipla</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare <span className="font-bold">{selectedIds.length} clienti</span>?
              <br />
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-bulk-delete-clienti-btn"
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Elimina {selectedIds.length} Clienti
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
